import links from 'common/helpers/links'
import constants from 'common/helpers/constants'
import getLinkWithParams from 'common/helpers/getters/getLinkWithParams'
import getSubscriptionStatuses from 'common/helpers/getters/getSubscriptionStatuses'
import { array, object, string } from 'common/helpers'


interface ITokensInput {
  name: string
  value: string
}

interface ITokensOutput {
  [token: string]: string
}

const modifyTokens = (_tokens: ITokensInput[] = []): ITokensOutput => {
  const tokens = {}

  _tokens.forEach(({ name, value }) => {
    let token = name.replace(/^sc|Token$/g, '')

    if (token === 'FacebookSigninTokenManualEmail') {
      token = 'facebookSigninManualEmail'
    }
    if (token === 'RedeemPromoCoupon') {
      token = 'redeemCoupon'
    }
    else {
      token = token.charAt(0).toLowerCase() + token.slice(1)
    }

    tokens[token] = value
  })

  return tokens
}

interface IAbTestsInput {
  name: string
  value: string
  mixpanelProp: string
}

interface IAbTestsOutput {
  [name: string]: {
    value: string
    mixpanelProp: string
  }
}

const modifyAbTests = (_abTests: IAbTestsInput[] = []): IAbTestsOutput => {
  const abTests = {}

  _abTests.forEach(({ name, ...props }) => abTests[name] = props)

  return abTests
}

interface IFeaturesInput {
  name: string
  enabled: boolean
}

interface IFeaturesOutput {
  [name: string]: boolean
}

const modifyFeatures = (_features: IFeaturesInput[]): IFeaturesOutput => {
  const features = {}

  _features.forEach(({ name, enabled }) => features[name] = enabled)

  return features
}

interface ISubscriptionInput {
  id?: number
  plan?: IPlan
  inCart?: boolean
  frequencyChangeable?: boolean
  upgradable?: boolean
  skippable?: boolean
  cancelable?: boolean
  status?: {
    value?: string
    subscribed?: boolean
    statusText?: string
  }
  addressStatus?: string
  [key: string]: any
}

interface IPlan {
  name?: string
  shippingPeriod?: number
  billingPeriod?: number
  description?: string
  productPrice?: number
  productsPerPeriod?: number
  productCount?: number
  totalPrice?: number
  price?: number
}

export interface ISubscriptionOutput {
  currPlan: IPlan
  id: number
  isSubscribed: boolean
  isUpgradable: boolean
  isSkippable: boolean
  isCancelable: boolean
  isFrequencyChangeable: boolean
  status: string
  statusText: string
  isMonthlyPlan: boolean
  isUpgradableForMonthlyPlan: boolean
  isShippingAddressInvalid: boolean
  [key: string]: any
}

const modifySubscription = ({
  id,
  plan,
  inCart, // eslint-disable-line
  frequencyChangeable,
  upgradable,
  skippable,
  cancelable,
  status: { value, subscribed, statusText }, // by graphql
  subscriptionStatus, // by rest/subscription
  subscriptionDate,
  addressStatus,
  ...rest
}: ISubscriptionInput = {}): ISubscriptionOutput => {
  // const upgradable = true

  const currPlan: IPlan = plan || {}
  const { name: currentPlanName } = currPlan
  const status = value || subscriptionStatus
  const isMonthlyPlan = currentPlanName === constants.planNames.monthly[1]
  const isUpgradableForMonthlyPlan = upgradable && isMonthlyPlan
  const subscriptionStatuses = getSubscriptionStatuses({ status, addressStatus })

  const isShippingAddressInvalid = (
    addressStatus === constants.addressStatuses.undeliverable
    || addressStatus === constants.addressStatuses.unableManuallyValidate
  )

  return {
    id,
    status,
    statusText,
    currPlan,
    isMonthlyPlan,
    isUpgradableForMonthlyPlan,
    isSubscribed: Boolean(subscribed || rest.subscribed),
    isUpgradable: upgradable,
    isSkippable: skippable,
    isCancelable: cancelable,
    isFrequencyChangeable: frequencyChangeable,
    isShippingAddressInvalid,
    subscriptionDate,
    ...subscriptionStatuses,
    ...rest,
  }
}

interface IClosedPopupsInput {
  name: string
  count: number
  lastUpdate: string
}

interface IClosedPopupsOutput {
  [name: string]: {
    count: number
    lastUpdate: string
  }
}

const modifyClosedPopups = (_closedPopups: IClosedPopupsInput[] = []): IClosedPopupsOutput => {
  const closedPopups = {}

  _closedPopups.forEach(({ name, count, lastUpdate }) => {
    if (!count) {
      return
    }

    closedPopups[name] = {
      count,
      lastUpdate,
    }
  })

  return closedPopups
}

type Product = {
  id: number
  image: string
  brand: string
  name: string
  amount: number
  url: string // product link
  volume: number
  volumeUnit: string
  category: string
  type: string
  offer?: string
  price: number
  discount: number
  totalPrice: number
  totalPriceWithDiscount: number
  needDiscountOffer: boolean // TODO ask backend how it will work in Recurly - added on 5/12/20 by pavelivanov
}

type CartPayload = {
  items: {
    subscription?: {
      name: string
      amount: number
      size: number
      volume: number
      volumeUnit: string
      volumeNotes: string
      price: number
    }
    products?: {
      id: number
      amount: number
      items: {
        id: number
        amount: number
        [key: string]: any
      }[]
    }[]
  }
  discount: number
  subtotal: number
  shipping: { [key: string]: any }
  price: number
  appliedPriceRules: { [key: string]: any }
  recommendationTag: string
  flashSaleRemoved: boolean
}

export type ModifiedCartResult = {
  items: {
    subscription: {
      name: string
      amount: number
      size: number
      volume: number
      volumeUnit: string
      volumeNotes: string
      price: number
    }
    products: {
      ids: number[]
      items: {
        [id: string]: Product
      }
    }
  }
  discount: number
  shipping: { [key: string]: any }
  isSubscriptionExist: boolean
  areProductsExist: boolean
  isFlashSaleRemoved: boolean
  isFlashSaleExist: boolean
  totalCount: number
  subtotal: number
  totalPrice: number
  appliedPriceRules: { [key: string]: any }
  recommendationTag: string
}

const modifyCart = (values: CartPayload): ModifiedCartResult => {
  const {
    items: {
      subscription,
      products: _products,
    } = {},
    subtotal, discount, shipping, price,
    appliedPriceRules, recommendationTag, flashSaleRemoved,
  } = values

  let products: any = _products

  // gql retruns array instead of object
  if (_products instanceof Array) {
    products = _products.reduce((result, item) => ({
      ...result,
      [item.id]: item,
    }), {})
  }

  type NormalizedProducts = {
    ids: number[]
    items: {
      [id: string]: Product
    }
  }

  // normalize products data structure for better performance
  const normalizedProducts: NormalizedProducts = {
    ids: Object.keys(products).map((id) => Number(id)),
    items: products,
  }

  const productsData = Object.values(normalizedProducts.items)
  const productsCount = productsData.reduce((sum: number, { amount }) => sum + Number(amount), 0)

  const totalCount            = productsCount + (subscription ? 1 : 0)
  const isSubscriptionExist   = Boolean(subscription)
  const areProductsExist      = Boolean(productsCount)
  const isFlashSaleRemoved    = Boolean(flashSaleRemoved)
  const isFlashSaleExist      = !isFlashSaleRemoved && productsData.some((item) => item.type === 'FlashSale')

  return {
    items: {
      subscription,
      products: normalizedProducts,
    },
    discount,
    shipping,
    subtotal,
    isSubscriptionExist,
    areProductsExist,
    isFlashSaleExist,
    isFlashSaleRemoved,
    totalCount,
    totalPrice: price,
    appliedPriceRules,
    recommendationTag,
  }
}

// interface IModifyProductInput {
//   tradingItems: {
//     id: number
//     tradingId: number
//     type
//     price
//     inStock
//     volume: {
//       volume: number
//       unit: string
//     }
//     image: string
//     productGroup: string
//     color: string
//     colorName: string
//     priceWithDiscount: number
//   }[]
//   visibility: boolean
//   tagTypes: {
//     topTagId: number
//     tags: {
//       id: number
//       voteCount: number
//     }[]
//   }[]
//   sex: 'female' | 'male' | 'unisex'
//   typeGroup: ValueOf<typeof constants.productTypeGroups>
//   isLimitedEdition: boolean
//   descriptionSections: {
//     id: number
//     title: string
//     text: string
//     data: { name: string, url: string }[]
//   }[]
//   texts: { [id: string]: string }
//   heroMedia: object[]
//   heroImages: { images: object }[]
//   makeupMedia: {
//     bigImage: string
//     preview: string
//     thumb: string
//     type: string
//     key: string
//     name: string
//   }[]
//   ratings: {
//     avgRate: number
//     count: number
//     distribution: object[]
//   }
//   isProductPageGQLEnabled: boolean
// }
//
// interface IModifyProductOutput {
//   sex: 'female' | 'male' | 'unisex'
//   typeGroup: ValueOf<typeof constants.productTypeGroups>
//   isLimitedEdition: boolean
//   volumes: {
//     actualType: string
//     type: string
//     volume: string
//     volumeUnit: string
//   }[]
//   tagTypes: {
//     topTagId: number
//     tags: {
//       id: number
//       voteCount: number
//     }[]
//     topTagVoteCount?: number
//   }[]
//   isVisible: boolean
//   isGenderVisible: boolean
//   texts: { [id: string]: string }
//   heroImages: { images: object }[]
//   makeupMedia: any, // TODO: change that after isProductPageGQLEnabled deleted
//   rating: {
//     average: number
//     reviewCount: number
//     reviews: object[]
//   }
//   // makeupMedia: {
//   //   name: string
//   //   images: {
//   //     bigImage: string
//   //     preview: string
//   //     thumb: string
//   //     type: string
//   //     key: string
//   //   }
//   // }[],
// }

const modifyProduct = (props) => {
  let {
    visibility,
    tradingItems,
    ratings,
    tagTypes, sex, typeGroup, isLimitedEdition,
    descriptionSections: _descriptionSections, texts: _texts, heroMedia, heroImages,
    makeupMedia: _makeupMedia,
    meta, media,
    ...rest
  } = props

  let volumes
  let isVisible

  // GQL returns meta: null ...
  if (!meta) {
    meta = {}
  }

  if (Array.isArray(tagTypes)) {
    tagTypes = tagTypes.map((tagType) => {
      const { topTagId } = tagType
      const topTag = tagType.tags.find((tag) => tag.id === topTagId)
      const topTagVoteCount = topTag ? topTag.voteCount : undefined

      return {
        ...tagType,
        topTagVoteCount,
      }
    })
  }
  else {
    tagTypes = []
  }

  // hide gender labels for Makeup and Candles
  const isGenderVisible = Boolean(
    sex
    && !isLimitedEdition
    && typeGroup !== constants.productTypeGroups.homeFragrance
    && typeGroup !== constants.productTypeGroups.makeup
    // extras
    && typeGroup !== constants.productTypeGroups.perfumeCase
    && typeGroup !== constants.productTypeGroups.candleSet
    && typeGroup !== constants.productTypeGroups.fragranceStorageBox
  )

  let texts = _texts

  if (Array.isArray(_descriptionSections)) {
    texts = _descriptionSections.reduce((accumulator: { [id: number]: string }, current: typeof _descriptionSections[0]) => {
      accumulator[current.id] = current.text || current.data

      return accumulator
    }, {})
  }

  if (Array.isArray(heroMedia)) {
    heroImages = heroMedia.map((media) => ({ images: { ...media } }))
  }

  type ProcessedMakeupMedia = {
    name: string,
    images: Omit<typeof _makeupMedia[0], 'name'>
  }

  let makeupMedia: any = _makeupMedia
  let processedMakeupMedia: ProcessedMakeupMedia

  processedMakeupMedia = makeupMedia.reduce((accumulator: ProcessedMakeupMedia, current: typeof _makeupMedia[0]) => {
    const { bigImage, preview, thumb, type, key, name } = current

    const isMediaExists = Boolean(accumulator[key])

    const media = {
      name,
      images: {
        bigImage,
        preview,
        thumb,
        type,
      },
    }

    accumulator[key] = isMediaExists ? [ ...accumulator[key], media ] : [ media ]

    return accumulator
  }, {})

  volumes = tradingItems.map(({ type, image, volume: { volume, unit: volumeUnit }, ...rest }) => ({
    ...rest,
    img: image,
    actualType: type,
    type: /Vial/.test(type) ? 'subscription' : 'onetime',
    volume,
    volumeUnit,
  }))

  const rating = {
    average: ratings.avgRate,
    reviewCount: ratings.count,
    reviews: ratings.distribution,
  }

  isVisible = (
    visibility === constants.productVisibility.visible
    || visibility === constants.productVisibility.outOfStock
    || visibility === constants.productVisibility.landing
  )

  const isLEPOutOfStock = isLimitedEdition && (
    rest.labels.includes('sold-out')
  )

  return {
    ...rest,
    media: media.filter((mediaItem) => mediaItem.type !== 'pdf'),
    rating,
    sex,
    typeGroup,
    isLimitedEdition,
    volumes,
    tagTypes,
    isVisible,
    isGenderVisible,
    isLEPOutOfStock,
    texts,
    meta,
    heroImages,
    makeupMedia: processedMakeupMedia || _makeupMedia,
  }
}

interface IModifyMakeupIntroProductsInput {
  name: string
  products: {
    colors: string[]
    product: {
      id: number
      img: string
      name: string
      brand: string
      retailPriceForcedVisibility: boolean
    }
  }[]
}

interface IModifyMakeupIntroProductsOutput {
  [name: string]: {
    colors: string[]
    id: number
    img: string
    name: string
    brand: string
    retailPriceForcedVisibility: boolean
  }[]
}

// TODO: add unit test
const modifyMakeupIntroProducts = (products: IModifyMakeupIntroProductsInput[]): IModifyMakeupIntroProductsOutput => {
  const newProducts = products.reduce((result, curr) => ({
    ...result,
    [curr.name]: curr.products.map((obj) => ({ ...obj.product, colors: obj.colors })),
  }), {})

  return newProducts
}

const modifyFlashSale = (data) => {
  const { id, timeLeft, tradingItem, media } = data

  return {
    ...data,
    isExpired: !timeLeft,
    tradingId: tradingItem && tradingItem.id ? tradingItem.id : id,
    media: (media || []).map(({ preview }) => preview),
  }
}

const modifyFlashSaleList = (flashSaleList) => {
  const flashSales = flashSaleList.filter((item) => item && item.id)

  // backend returns 2 items only if both 'male' and 'female' ara launched
  const isTwoFlashSalesAvailable = flashSales.length === 2
  const isExpired = !flashSales.length

  let defaultValue = {
    timeLeft: 0,
    isFetched: true,
    isExpired,
    isTwoFlashSalesAvailable,
    data: {},
  }

  if (isExpired) {
    return defaultValue
  }

  return flashSales.reduce((result, current) => {
    // if some of flashSale is expired then all are expired
    // from the task: "if we launch 2 flash sales in the moment, the dates should be the same"
    if (!result.isExpired) {
      const currentFlashSale = modifyFlashSale(current)

      const { gender, timeLeft, startDate, isExpired } = currentFlashSale

      result.data[gender] = currentFlashSale
      result.isExpired = isExpired
      result.timeLeft = timeLeft
      result.startDate = startDate
    }

    return result
  }, defaultValue)
}

const modifyBrandsByAlphabet = (brands) => (
  brands
    .map(({ brand, slug }) => {
      const firstLetter = brand.match(/([\dA-Z])/i)[0].toUpperCase()
      const isNumber    = /\d/.test(firstLetter)
      const to          = getLinkWithParams(links.brand, { slug: slug || brand })

      return {
        brand,
        to,
        firstLetter,
        isNumber,
      }
    })
    .sort((a, b) => {
      if (a.firstLetter === b.firstLetter) {
        return a.brand.localeCompare(b.brand)
      }

      const isDown  = !a.isNumber && b.isNumber || a.firstLetter < b.firstLetter
      const isUp    = a.isNumber && !b.isNumber || a.firstLetter > b.firstLetter

      return isDown ? -1 : isUp ? 1 : 0
    })
    .reduce((groups, { brand, to, firstLetter }) => {
      const groupIndex  = groups.findIndex(({ letter }) => letter === firstLetter)
      const brandData   = { brand, to }

      if (groupIndex > -1) {
        groups[groupIndex].brands.push(brandData)
      }
      else {
        groups.push({
          id: `letter_${firstLetter}`,
          letter: firstLetter,
          brands: [ brandData ],
        })
      }

      return groups
    }, [])
)

const modifyBrandProductsByCategory = ({ products, gender }) => {
  const isMale = gender === 'male'

  const fragranceGroups = {
    male: 'cologne',
    female: 'perfume',
    unisex: 'unisex',
  }

  const { unisex, perfume, cologne, ...cosmetics } = products.reduce((result, product) => {
    const { typeGroup, type, sex } = product

    const category = typeGroup === 'Perfume' ? fragranceGroups[sex] : string.decapitalize(type)

    result[category] = result[category] || { category, products: [] }
    result[category].products.push(product)

    return result
  }, {})

  const fragranceCategories = isMale ? [ unisex, cologne, perfume ] : [ unisex, perfume, cologne ]
  const cosmeticCategories  = Object.keys(cosmetics).map((key) => cosmetics[key])

  return fragranceCategories.filter((item) => item).concat(cosmeticCategories)
}


export {
  modifyCart,
  modifyTokens,
  modifyAbTests,
  modifyProduct,
  modifyFeatures,
  modifySubscription,
  modifyClosedPopups,
  modifyFlashSaleList,
  modifyBrandsByAlphabet,
  modifyMakeupIntroProducts,
  modifyBrandProductsByCategory,
}
